/**
* Ellipsis Title Directive
*
* Applies a native browser `title` tooltip only when the host element's text
* is visually truncated due to lack of available space.
*/

import { AfterViewInit, Directive, ElementRef, HostBinding, HostListener, inject, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

/**
* This directive is intended for reusable UI text areas where:
* - text is rendered with CSS ellipsis
* - full value should be available on hover/focus
* - unnecessary tooltips should be avoided for non-truncated text
*
* Supported scenarios:
* - single-line truncation by width
* - optional height-based truncation checks for multi-line content
* - automatic updates on content changes, resize, hover, and focus
*
* @example
* <span class="groups-panel__name" [fgaEllipsisTitle]="group.name">
*   {{ group.name }}
* </span>
*
* @example
* <h2 class="group-details__title" [fgaEllipsisTitle]="group.name">
*   {{ group.name }}
* </h2>
*/

@Directive({
  selector: '[fgaEllipsisTitle]'
})
export class EllipsisTitleDirective implements AfterViewInit, OnChanges, OnDestroy {
  /**
   * Explicit tooltip text to display when truncation is detected.
   * If not provided, the directive falls back to the host element text content.
   */
  @Input('fgaEllipsisTitle')
  tooltipText?: string | null;

  /**
   * Disables tooltip handling completely.
   * When true, the `title` attribute is removed from the host element.
   */
  @Input()
  fgaEllipsisTitleDisabled = false;

  /**
   * Enables height overflow checks in addition to width overflow checks.
   * Useful for multi-line truncation scenarios.
   *
   * Default behavior checks width only, which is sufficient for standard
   * single-line ellipsis usage.
   */
  @Input()
  fgaEllipsisTitleCheckHeight = false;

  // =========================================================
  // Host bindings
  // =========================================================

  @HostBinding('attr.title') hostTitle: string | null = null;

  // =========================================================
  // Local state
  // =========================================================

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly ngZone = inject(NgZone);

  // =========================================================
  // Lifecycle
  // =========================================================

  ngAfterViewInit(): void {
    this.setupObservers();
    this.updateTitle();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['tooltipText'] ||
      changes['fgaEllipsisTitleDisabled'] ||
      changes['fgaEllipsisTitleCheckHeight']
    ) {
      queueMicrotask(() => this.updateTitle());
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  // ===========================================================================
  // Handlers
  // ===========================================================================

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.updateTitle();
  }

  @HostListener('focus')
  onFocus(): void {
    this.updateTitle();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateTitle();
  }

  // =========================================================
  // Helpers
  // =========================================================

  private setupObservers(): void {
    const element = this.elementRef.nativeElement;
    this.ngZone.runOutsideAngular(() => {
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateTitle();
        });
        this.resizeObserver.observe(element);
      }
      if (typeof MutationObserver !== 'undefined') {
        this.mutationObserver = new MutationObserver(() => {
          this.updateTitle();
        });
        this.mutationObserver.observe(element, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    });
  }

  private updateTitle(): void {
    const element = this.elementRef.nativeElement;

    if (this.fgaEllipsisTitleDisabled) {
      this.hostTitle = null;
      return;
    }

    const text = (this.tooltipText ?? element.textContent ?? '').trim();
    if (!text) {
      this.hostTitle = null;
      return;
    }

    const isOverflowingWidth = element.scrollWidth > element.clientWidth;
    const isOverflowingHeight = element.scrollHeight > element.clientHeight;
    const isTruncated = this.fgaEllipsisTitleCheckHeight
      ? isOverflowingWidth || isOverflowingHeight
      : isOverflowingWidth;
    this.hostTitle = isTruncated ? text : null;
  }
}